import { DownloadIcon, TrashIcon } from '@radix-ui/react-icons'
import useMessage from 'antd/es/message/useMessage'
import CantileverButton from 'components/CantileverButton'
import { AssetOverviewDataContext } from 'features/assetDatabase/contexts/AssetOverviewDataLayer'
import { useAxisNames, useGeckoLocale } from 'features/locale'
import React, { useContext } from 'react'

import { useImagePreviewerContext } from './ImagePreviewerContext'
import { useImagesContext } from './ImagesContext'
import { ImagesInputRow } from './ImagesInputRow'
import {
  ISDescription,
  ISFullSizedImage,
  ISHeaderContainer,
  ISIconButtonsRow,
  ISImageContainer,
  ISImageWrapper,
  ISInputDisplay,
  ISInputRow,
  ISInputsContainer,
} from './StyledComponents'
import { convertCoordstoLocalUnits, getComponentModelType } from './utils'

export const ModalImageSide = () => {
  const { assetDataObj } = useContext(AssetOverviewDataContext)
  const { inspections } = useImagesContext()
  const {
    imageUrl,
    imageThumbnail,
    componentID,
    inspectionID,
    editingState,
    setEditingState,
    formState,
    setFormState,
    handleDeleteClick,
    selectedCoords,
    inspection,
  } = useImagePreviewerContext()

  const [messageApi, ContextHolder] = useMessage()
  const { getPreferredMeasurementUnit } = useGeckoLocale()

  const { x, y } = selectedCoords
    ? convertCoordstoLocalUnits(selectedCoords, getPreferredMeasurementUnit)
    : { x: null, y: null }

  const componentModelJson = getComponentModelType(
    inspections,
    componentID,
    inspectionID,
    assetDataObj
  )

  const { xAxisName, yAxisName } = useAxisNames(
    selectedCoords,
    componentModelJson?.type
  )

  const handleDescriptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormState({
      ...formState,
      fileCaption: e.target.value,
    })
  }

  const handleFileNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormState({
      ...formState,
      fileName: e.target.value,
    })
  }

  const setIsEditingFileName = (value: boolean) => {
    setEditingState({
      ...editingState,
      fileName: value,
    })
  }

  const setIsEditingDescription = (value: boolean) => {
    setEditingState({
      ...editingState,
      fileCaption: value,
    })
  }

  const handleDownload = async () => {
    const isFileNameSame =
      formState.fileName + '.' + imageThumbnail.data.fileType ===
      imageThumbnail.data.visibleName
    const downloadFilename = isFileNameSame
      ? imageThumbnail.data.visibleName
      : formState.fileName + '.' + imageThumbnail.data.fileType
    try {
      // Fetch the image data
      const response = await fetch(imageUrl)
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      // Create a blob from the response
      const blob = await response.blob()
      const blobUrl = window.URL.createObjectURL(blob)

      // Create a temporary anchor element
      const link = document.createElement('a')
      link.href = blobUrl
      link.setAttribute('download', downloadFilename) // Set the desired filename

      // Append to the document, click it, and remove it
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      // Clean up the object URL
      window.URL.revokeObjectURL(blobUrl)
    } catch (error) {
      messageApi.error(`Failed to download image: ${error}`)
    }
  }

  return (
    <ISImageContainer>
      {ContextHolder}
      <ISHeaderContainer>
        {' '}
        <ImagesInputRow
          label='File Name:'
          value={formState.fileName}
          isEditing={editingState.fileName}
          setIsEditing={setIsEditingFileName}
          onChange={handleFileNameChange}
          ariaLabel='Edit file name'
        />
        <ISInputRow>
          <ISDescription>
            {imageThumbnail.data.fileType.toUpperCase()}
          </ISDescription>
          <div>•</div>
          <ISDescription>
            {imageThumbnail.data.upload_date?.toISOString().split('T')[0]}
          </ISDescription>
        </ISInputRow>
      </ISHeaderContainer>
      <ISImageWrapper>
        <ISFullSizedImage src={imageUrl} />
      </ISImageWrapper>
      <ISIconButtonsRow>
        <CantileverButton iconOnly onClick={handleDownload}>
          <DownloadIcon />
        </CantileverButton>
        <CantileverButton iconOnly onClick={handleDeleteClick}>
          <TrashIcon />
        </CantileverButton>
      </ISIconButtonsRow>
      <ISInputsContainer>
        <ImagesInputRow
          label='Description:'
          value={formState.fileCaption}
          isEditing={editingState.fileCaption}
          setIsEditing={setIsEditingDescription}
          onChange={handleDescriptionChange}
          ariaLabel='Edit description'
        />
        {inspection && (
          <>
            <ISInputRow>
              <ISDescription>Coordinates:</ISDescription>
              <ISInputDisplay>
                {' '}
                {x
                  ? '(' + x.num.toFixed(2).toString() + ' ' + x.unit.name + ','
                  : ''}{' '}
                {y
                  ? y.num.toFixed(2).toString() + ' ' + y.unit.name + ')'
                  : "Click to set the image's location →"}
              </ISInputDisplay>
            </ISInputRow>
            {componentModelJson?.type === 'ship_deck' && (
              <>
                <ImagesInputRow
                  label={xAxisName}
                  value={
                    x
                      ? x.abs().num.toFixed(2).toString() + ' ' + x.unit.name
                      : ''
                  }
                  isEditing={false}
                  setIsEditing={() => {}}
                  onChange={() => {}}
                  displayOnly={true}
                />
                <ImagesInputRow
                  label={yAxisName}
                  value={
                    y
                      ? y.abs().num.toFixed(2).toString() + ' ' + y.unit.name
                      : ''
                  }
                  isEditing={false}
                  setIsEditing={() => {}}
                  onChange={() => {}}
                  displayOnly={true}
                />
              </>
            )}
          </>
        )}
      </ISInputsContainer>
    </ISImageContainer>
  )
}
